// Les calculs de rémunération (issue #206, lot 1 du plan salaire et entretien,
// `docs/analyse-package-entretien.md`). Code pur : le modèle cherche et cite,
// ces fonctions comptent. Au banc du 2026-10-16, le modèle reconnaissait qu'un
// repère valait pour 15 ans d'expérience et construisait sa fourchette dessus
// quand même : une fourchette n'est donc jamais la sienne, elle naît ici, de
// deux repères relus qui s'accordent.
//
// Les conventions dépendent du PAYS du poste (retour de Nicolas du 2026-10-16 :
// une offre à Lausanne recevait des repères belges). Un brut mensuel belge fait
// 13,92 mois par an (treizième mois et double pécule, celles de
// `criteresPrompt`), un suisse 13 (le treizième salaire y est la règle), un
// néerlandais 12,96 (8 % de pécule) ; un temps plein va de 35 heures (France)
// à 42 (Suisse). Tout s'affiche en BRUT ANNUEL, l'unité qui se compare d'un
// pays à l'autre. Aucun net : il dépend de la situation familiale et du pays.

export type Periode = 'heure' | 'mois' | 'an'

export interface Montant {
  min: number
  max: number
  periode: Periode
}

export interface Conventions {
  moisParAn: number
  heuresSemaine: number
  devise: string
}

export const CONVENTIONS: Record<string, Conventions> = {
  BE: { moisParAn: 13.92, heuresSemaine: 38, devise: 'EUR' },
  CH: { moisParAn: 13, heuresSemaine: 42, devise: 'CHF' },
  LU: { moisParAn: 12, heuresSemaine: 40, devise: 'EUR' },
  FR: { moisParAn: 12, heuresSemaine: 35, devise: 'EUR' },
  NL: { moisParAn: 12.96, heuresSemaine: 40, devise: 'EUR' },
  DE: { moisParAn: 12, heuresSemaine: 40, devise: 'EUR' },
  GB: { moisParAn: 12, heuresSemaine: 37.5, devise: 'GBP' },
}
const PAR_DEFAUT: Conventions = { moisParAn: 12, heuresSemaine: 40, devise: 'EUR' }

export function conventionsDe(pays: string | null | undefined): Conventions {
  return CONVENTIONS[String(pays ?? '').toUpperCase()] ?? PAR_DEFAUT
}

/** Les conventions belges, gardées sous leur ancien nom. */
export const MOIS_PAR_AN = CONVENTIONS.BE.moisParAn
export const HEURES_SEMAINE = CONVENTIONS.BE.heuresSemaine

const versMois = (p: Periode, c: Conventions) => (p === 'heure' ? (c.heuresSemaine * 52) / 12 : p === 'mois' ? 1 : 1 / c.moisParAn)

/** Le même brut, sur une autre période, selon les conventions du pays (la
 *  Belgique sans pays). */
export function convertir(montant: number, de: Periode, vers: Periode, pays?: string | null): number {
  const c = pays === undefined ? CONVENTIONS.BE : conventionsDe(pays)
  return (montant * versMois(de, c)) / versMois(vers, c)
}

export function convertirMontant(m: Montant, vers: Periode, pays?: string | null): Montant {
  return { min: convertir(m.min, m.periode, vers, pays), max: convertir(m.max, m.periode, vers, pays), periode: vers }
}

/**
 * Le pays d'une offre, lu dans son lieu, son titre ou le début de son texte,
 * ou null quand rien ne le dit. Les cantons suisses s'écrivent « (Lausanne,
 * VD) » : on ne les lit qu'ainsi, « BE » seul étant aussi la Belgique.
 */
export function paysDeLOffre(lieu: string | null, titre: string, description = ''): string | null {
  const plat = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const motifs: [string, RegExp][] = [
    ['CH', /\b(suisse|switzerland|schweiz|svizzera|lausanne|geneve|geneva|genf|zurich|bern|berne|bale|basel|lugano|neuchatel|winterthur|zoug|zug)\b|, (vd|ge|zh|bs|ti|ne|fr|vs|zg|lu)\)/],
    // La province belge de Luxembourg avant le Grand-Duché.
    ['BE', /province de luxembourg|\b(arlon|bastogne|marche-en-famenne|neufchateau|libramont|virton)\b/],
    ['LU', /\b(grand-duche|esch-sur-alzette|kirchberg|bertrange|strassen|luxembourg)\b/],
    ['FR', /\b(france|paris|lyon|lille|marseille|toulouse|nantes|bordeaux|strasbourg)\b/],
    ['NL', /\b(nederland|netherlands|pays-bas|amsterdam|rotterdam|utrecht|eindhoven|den haag|the hague)\b/],
    ['DE', /\b(deutschland|germany|allemagne|berlin|munich|munchen|frankfurt|hamburg|koln|cologne|dusseldorf|aachen)\b/],
    ['GB', /\b(united kingdom|royaume-uni|london|londres|manchester)\b/],
    ['BE', /\b(belgique|belgium|belgie|bruxelles|brussels|brussel|anvers|antwerpen|antwerp|gand|gent|ghent|liege|namur|charleroi|louvain|leuven|mons|wavre|zaventem|mechelen|malines|hasselt|brugge|bruges|arlon)\b|\b(be|belgium)\b\s*$|, be\b/],
  ]
  // Le lieu et le titre d'abord : une offre belge peut citer un bureau à Paris.
  for (const t of [plat(`${lieu ?? ''} | ${titre}`), plat(description.slice(0, 600))]) {
    for (const [pays, re] of motifs) if (re.test(t)) return pays
  }
  return null
}

/** Un 4/5 : le brut d'un temps plein, au prorata. */
export function quatreCinquiemes(m: Montant): Montant {
  return { min: m.min * 0.8, max: m.max * 0.8, periode: m.periode }
}

/**
 * La période dite par un texte (« brut mensuel », « par heure », « per jaar »,
 * « €/h »), ou null. Un texte qui en dit deux n'en dit aucune.
 */
export function lirePeriode(texte: string): Periode | null {
  const t = texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const dit: Periode[] = []
  if (/horaire|par heure|\/ ?h\b|\/ ?heure|per uur|\/ ?uur|uurloon|hourly|per hour|an hour/.test(t)) dit.push('heure')
  if (/mensuel|par mois|\/ ?mois|per maand|maandelijks|\/ ?maand|monthly|per month|a month|\/ ?month/.test(t)) dit.push('mois')
  if (/annuel|par an\b|\/ ?an\b|per jaar|jaarlijks|\/ ?jaar|annual|yearly|per year|a year|\/ ?year|p\.a\./.test(t)) dit.push('an')
  return dit.length === 1 ? dit[0] : null
}

/**
 * Les montants écrits dans un texte, dans l'ordre : « 4.500 », « €4,500 »,
 * « 65 000 », « 15,82 », « 65k ». Un séparateur suivi de trois chiffres est
 * un séparateur de milliers ; suivi d'un ou deux, une décimale.
 */
export function montantsDans(texte: string): number[] {
  const re = /(\d{1,3}(?:[ .,  ']\d{3})+|\d+)(?:[.,](\d{1,2}))?(?!\d)(\s?[kK]\b)?/g
  const sortie: number[] = []
  for (const m of texte.matchAll(re)) {
    const entier = Number(m[1].replace(/[ .,  ']/g, ''))
    const n = m[2] ? entier + Number(`0.${m[2]}`) : entier
    sortie.push(m[3] ? n * 1000 : n)
  }
  return sortie
}

/**
 * Les chiffres d'un repère sont-ils dans sa phrase citée ? Le minimum et le
 * maximum doivent s'y lire, à un demi pour cent près (arrondis). Un repère
 * dont le chiffre n'est pas dans l'extrait est un chiffre du modèle, pas de
 * la source.
 */
export function chiffresCites(m: Pick<Montant, 'min' | 'max'>, extrait: string): boolean {
  const lus = montantsDans(extrait)
  const lu = (v: number) => lus.some((x) => Math.abs(x - v) <= Math.max(0.01, v * 0.005))
  return lu(m.min) && lu(m.max)
}

/**
 * L'offre annonce-t-elle moins que le minimum du secteur ? `entierement` quand
 * même son maximum est en dessous, `en_partie` quand seul le bas de sa
 * fourchette l'est (au banc, une offre de garage annonçait 15,10 à 19,10 € de
 * l'heure pour un minimum de 15,82 €). La comparaison se fait sur la période
 * du barème ; l'écart est celui du bas annoncé, en part du minimum (0,05 pour
 * 5 %). Un écart d'un millième est un arrondi, pas une infraction.
 */
export function sousLeMinimum(
  annonce: Montant,
  minimum: Pick<Montant, 'min' | 'periode'>,
  pays?: string | null
): { sous: 'non' | 'en_partie' | 'entierement'; ecart: number } {
  const bas = convertir(annonce.min, annonce.periode, minimum.periode, pays)
  const haut = convertir(annonce.max, annonce.periode, minimum.periode, pays)
  const ecart = (minimum.min - bas) / minimum.min
  if (ecart <= 0.001) return { sous: 'non', ecart: 0 }
  return { sous: (minimum.min - haut) / minimum.min > 0.001 ? 'entierement' : 'en_partie', ecart }
}

/** Deux montants s'accordent quand leurs fourchettes se touchent, à
 *  `tolerance` près de part et d'autre. */
function saccordent(a: Montant, b: Montant, tolerance: number): boolean {
  return a.min * (1 - tolerance) <= b.max * (1 + tolerance) && b.min * (1 - tolerance) <= a.max * (1 + tolerance)
}

/**
 * La fourchette qu'on peut afficher, ou null. Il en faut au moins DEUX qui
 * s'accordent : on garde le plus grand groupe de repères qui s'accordent tous
 * deux à deux, et sa fourchette est ce qu'ils ont en commun (le plus haut des
 * minimums, le plus bas des maximums). Si deux repères ponctuels voisins n'ont
 * rien en commun au sens strict, on prend l'intervalle qui les sépare.
 * Rendue sur la période demandée, arrondie à la centaine par an, à la dizaine
 * par mois, au centime par heure.
 */
export function fourchetteConcordante(
  reperes: readonly Montant[],
  options: { periode?: Periode; tolerance?: number; pays?: string | null } = {}
): (Montant & { sur: number }) | null {
  const periode = options.periode ?? 'an'
  const tolerance = options.tolerance ?? 0.1
  const r = reperes
    .filter((m) => Number.isFinite(m.min) && Number.isFinite(m.max) && m.min > 0 && m.max >= m.min)
    .slice(0, 12)
    .map((m) => convertirMontant(m, periode, options.pays))
  let meilleur: Montant[] = []
  for (let masque = 1; masque < 1 << r.length; masque++) {
    const groupe = r.filter((_, i) => masque & (1 << i))
    if (groupe.length <= meilleur.length) continue
    if (groupe.every((a, i) => groupe.every((b, j) => j <= i || saccordent(a, b, tolerance)))) meilleur = groupe
  }
  if (meilleur.length < 2) return null
  const bas = Math.max(...meilleur.map((m) => m.min))
  const haut = Math.min(...meilleur.map((m) => m.max))
  const pas = periode === 'an' ? 100 : periode === 'mois' ? 10 : 0.01
  const arrondi = (v: number) => Math.round(v / pas) * pas
  return { min: arrondi(Math.min(bas, haut)), max: arrondi(Math.max(bas, haut)), periode, sur: meilleur.length }
}
